import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getPerformance } from 'firebase/performance';
import { getRemoteConfig } from 'firebase/remote-config';
import { connectStorageEmulator, getStorage, ref } from 'firebase/storage';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';

const firebaseConfig = {
  apiKey: process.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VITE_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.VITE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VITE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VITE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VITE_APP_FIREBASE_MEASUREMENT_ID,
};

self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.VITE_APP_DEBUG_TOKEN;

const isDev = () => process.env.VITE_APP_ENVIRONMENT === 'LOCAL';

const app = initializeApp(firebaseConfig);

const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaEnterpriseProvider(
    process.env.VITE_APP_RECAPTCHA_TOKEN!
  ),
  isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
});
const auth = getAuth(app);

const googleProvider = new GoogleAuthProvider();

const storage = getStorage(app);
const perf = getPerformance(app);
// trace(perf, "measuringX")

const analytics = getAnalytics(app);
// logEvent(analytics)

const remoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis = 50000;

const functions = getFunctions(app);

if (isDev()) {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectStorageEmulator(storage, 'localhost', 9199);
}

const signInWithGoogle = () => signInWithPopup(auth, googleProvider);

const logInWithEmailAndPassword = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password);

const registerWithEmailAndPassword = (email: string, password: string) => {
  const oneUppercaseRegex = /^(?=.*[A-Z]).*$/;
  const lowerCaseRegex = /^(?=.*[a-z]).*$/;
  const atleastOneNumberRegex = /^(?=.*[0-9]).*$/;
  const oneSpecialCharacterRegex = /^(?=.*[!@#$%^&*]).*$/;
  let errorMessage = '';
  if (password.length < 8 || password.length > 32)
    errorMessage =
      'Password length should be a minimum of 8 and a maximum of 32';
  if (!errorMessage && !oneUppercaseRegex.test(password)) {
    errorMessage = 'Password must contain one upper case character';
  }
  if (!errorMessage && !lowerCaseRegex.test(password)) {
    errorMessage = 'Password must contain one lower case character';
  }
  if (!errorMessage && !atleastOneNumberRegex.test(password)) {
    errorMessage = 'Password must contain one number';
  }
  if (!errorMessage && !oneSpecialCharacterRegex.test(password)) {
    errorMessage = 'Password must contain one special character';
  }
  if (errorMessage) throw new Error(errorMessage);
  return createUserWithEmailAndPassword(auth, email, password);
};

const logout = async () => {
  const companyWant = localStorage.getItem('Company-want');
  const ContactWant = localStorage.getItem('Contacts-want');
  const addNewSection = localStorage.getItem('Add-New-Section-want');
  localStorage.clear();
  localStorage.setItem('Company-want', companyWant!);
  localStorage.setItem('Contacts-want', ContactWant!);
  localStorage.setItem('Add-New-Section-want', addNewSection!);
  await signOut(auth);
  // window.location.reload(); // Don't know why this was there
};

const triggerResetEmail = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
  console.log('Password reset email sent');
};

export {
  auth,
  appCheck,
  storage,
  remoteConfig,
  perf,
  analytics,
  functions,
  signInWithGoogle,
  logout,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  ref as storageRef,
  triggerResetEmail,
};
